import { type ReactElement, useLayoutEffect, useMemo, useRef } from 'react';
import { type ChargePowerSummary } from '../../services/battery-monitoring-api/types/battery-monitoring-api';
import { NotAvailable } from '../../NotAvailable';
import styles from './style.module.scss';
import drawPieChart, { type PieChartItem } from '@/utils/draw-pie-chart';

interface AvgChargePowerChartProperties {
  readonly chargePower?: ChargePowerSummary;
}

const CHARGE_POWER_ITEMS = [
  { label: '0 - 90 kW', color: 'var(--tds-blue-500)', dotClassName: 'dot-0-to-90', key: 'pct0To90Kw' },
  { label: '90 - 180 kW', color: 'var(--tds-blue-300)', dotClassName: 'dot-90-to-180', key: 'pct90To180Kw' },
  { label: '180 - 270 kW', color: 'var(--tds-blue-200)', dotClassName: 'dot-180-to-270', key: 'pct180To270Kw' },
  { label: '270+ kW', color: '#E0CFAD', dotClassName: 'dot-270-plus', key: 'pct270PlusKw' },
] as const;

const formatAverageChargePower = (value?: number | null): string => {
  if (value === undefined || value === null) {
    return 'N/A';
  }

  return `${Number.isInteger(value) ? value : value.toFixed(1)} kW`;
};

const AvgChargePowerChart = ({ chargePower }: AvgChargePowerChartProperties): ReactElement => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const averageChargePower = formatAverageChargePower(chargePower?.avgChargingPowerKw);

  const chartItems = useMemo<PieChartItem[]>(
    () =>
      CHARGE_POWER_ITEMS.map((item, index) => ({
        header: item.label,
        value: chargePower?.[item.key] ?? 0,
        color: item.color,
        segmentIndex: index,
      })),
    [chargePower],
  );

  const hasData = chartItems.some((item) => item.value > 0);

  useLayoutEffect(() => {
    const chartElement = chartRef.current;

    if (!chartElement || !hasData) {
      return;
    }

    drawPieChart({
      element: chartElement,
      chartItems,
      selectedSegment: null,
      indexKey: 'avgChargePower',
      innerRadius: 0,
      padAngle: 0,
      interactive: false,
    });
  }, [chartItems, hasData]);

  return (
    <div className={styles.container}>
      <div className={styles.title}>Charge Power [kW]</div>
      <div className={styles.content}>
        <div className={styles['chart-panel']}>
          {hasData ? (
            <div ref={chartRef} className={styles.chart} data-testid="avg-charge-power-chart" />
          ) : (
            <svg
              viewBox="0 0 200 200"
              className={styles['unavailable-chart']}
              data-testid="avg-charge-power-chart-unavailable"
              role="img"
              aria-label="Charge power not available"
            >
              <circle cx="100" cy="100" r="92" fill="var(--tds-grey-500)" />
              <text
                x="100"
                y="100"
                textAnchor="middle"
                dominantBaseline="middle"
                className={styles['unavailable-text']}
              >
                N/A
              </text>
            </svg>
          )}
        </div>
        <div className={styles.legend}>
          {chartItems.map((item, index) => (
            <div key={item.header} className={styles['legend-row']}>
              <div className={styles['legend-label']}>
                <span
                  className={`${styles['legend-dot']} ${hasData ? styles[CHARGE_POWER_ITEMS[index].dotClassName] : styles['dot-unavailable']}`}
                />
                <span>{item.header}</span>
              </div>
              <span>
                {typeof chargePower?.[CHARGE_POWER_ITEMS[index].key] === 'number' ? (
                  `${Math.round(item.value)} %`
                ) : (
                  <NotAvailable />
                )}
              </span>
            </div>
          ))}
        </div>
        <div className={styles['side-panel']}>
          <div className={styles.info}>
            <span className={styles['info-icon']}>i</span>
            <span className={styles['info-text']}>
              Avg. charge power: {averageChargePower === 'N/A' ? <NotAvailable /> : averageChargePower}
            </span>
          </div>
          <div className={styles.explanation}>
            <span className={styles['info-icon']}>i</span>
            <span className={styles['explanation-text']}>
              Shows how the truck&apos;s charging time is distributed across four distinct power categories.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvgChargePowerChart;
